import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  DocumentArrowDownIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  StarIcon,
  ScaleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import AnalyticsFilters from '../components/analytics/AnalyticsFilters';
import FinancialOverview from '../components/analytics/FinancialOverview';
import RevenueAnalysis from '../components/analytics/RevenueAnalysis';
import ExpenseAnalysis from '../components/analytics/ExpenseAnalysis';
import CashFlowAnalysis from '../components/analytics/CashFlowAnalysis';
import ProfitabilityAnalysis from '../components/analytics/ProfitabilityAnalysis';
import DepartmentComparison from '../components/analytics/DepartmentComparison';
import TrendAnalysis from '../components/analytics/TrendAnalysis';
import BudgetVariance from '../components/analytics/BudgetVariance';
import KPIsAnalysis from '../components/analytics/KPIsAnalysis';
import DashboardLayout from '../components/dashboard/DashboardLayout';

const AnalyticsPage: React.FC = () => {
  const { t, direction } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    dateRange: { start: '2024-01-01', end: '2024-12-31', preset: 'last_30_days' },
    departments: ['all'],
    metrics: ['revenue', 'expenses', 'profit'],
    detailLevel: 'summary',
    displayType: 'chart',
    currency: 'SAR'
  });


  const tabs = [
    { id: 'overview', name: 'نظرة عامة', icon: ChartBarIcon },
    { id: 'revenue', name: 'تحليل الإيرادات', icon: ArrowTrendingUpIcon },
    { id: 'expenses', name: 'تحليل المصروفات', icon: CurrencyDollarIcon },
    { id: 'cashflow', name: 'التدفق النقدي', icon: ArrowPathIcon },
    { id: 'profitability', name: 'تحليل الربحية', icon: ScaleIcon },
    { id: 'departments', name: 'مقارنة الأقسام', icon: BuildingOfficeIcon },
    { id: 'trends', name: 'تحليل الاتجاهات', icon: ArrowTrendingUpIcon },
    { id: 'budget', name: 'انحراف الميزانية', icon: CalculatorIcon },
    { id: 'kpis', name: 'مؤشرات الأداء', icon: StarIcon },
    { id: 'ratios', name: 'النسب المالية', icon: ChartBarIcon },
    { id: 'audit', name: 'سجل المراجعة', icon: ClockIcon }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <FinancialOverview filters={filters} />;
      case 'revenue':
        return <RevenueAnalysis filters={filters} />;
      case 'expenses':
        return <ExpenseAnalysis filters={filters} />;
      case 'cashflow':
        return <CashFlowAnalysis filters={filters} />;
      case 'profitability':
        return <ProfitabilityAnalysis filters={filters} />;
      case 'departments':
        return <DepartmentComparison filters={filters} />;
      case 'trends':
        return <TrendAnalysis filters={filters} />;
      case 'budget':
        return <BudgetVariance filters={filters} />;
      case 'kpis':
        return <KPIsAnalysis filters={filters} />;
      case 'ratios':
        return (
          <div className="text-center py-12">
            <ChartBarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">النسب المالية</h3>
            <p className="text-gray-600 dark:text-gray-400">قريباً...</p>
          </div>
        );
      case 'audit':
        return (
          <div className="text-center py-12">
            <ClockIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">سجل المراجعة</h3>
            <p className="text-gray-600 dark:text-gray-400">قريباً...</p>
          </div>
        );
      default:
        return <FinancialOverview filters={filters} />;
    }
  };

  return (
    <DashboardLayout currentPage="analytics">
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${direction === 'rtl' ? 'font-arabic' : ''}`}>
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="w-8 h-8 text-blue-500 ml-3 rtl:ml-0 rtl:mr-3" />
                  التحليلات المالية المتقدمة
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  تحليل شامل ومفصل للبيانات المالية مع أدوات متقدمة للمحاسبين والمدراء الماليين
                </p>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                  <DocumentArrowDownIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                  تصدير التقرير
                </button>
                <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg">
                  <Cog6ToothIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                  إعدادات التحليل
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <AnalyticsFilters
            initialFilters={filters}
            onFiltersChange={setFilters}
          />
        </div>

        {/* Analytics Categories Tabs */}
        <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 rtl:space-x-reverse overflow-x-auto pb-2">
            {tabs.map((category) => {
              const Icon = category.icon;
              return (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveTab(category.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex-shrink-0 flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === category.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
                  {category.name}
                </motion.button>
              );
            })}
          </div>
          
          {/* Active Category Description */}
          <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
            {tabs.find(cat => cat.id === activeTab)?.name}
          </div>
        </div>

        {/* Main Analytics Content */}
        <div className="px-6 py-6">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
